import React, { useEffect, useState } from "react";
import axios from "axios";
import { Typography, Paper, CircularProgress, Button } from "@mui/material";
import { Tabs, Tab } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme, useMediaQuery } from "@mui/material";

import { useLanguage } from "../context/LanguageContext.js";

import { logDev } from "../utils/logger.js";
import { buildStatsCards } from "../utils/statsCardsBuilder.js";

import GroupesParAnEtMois from "../components/admin/GroupesParAnEtMois.js";
import StatsCards from "../components/admin/StatsCards.js";

const AdminDashboard = ({ email }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { t , locale} = useLanguage();
  const token = localStorage.getItem("adminToken");
  
  const statsCards = stats
    ? buildStatsCards(stats, t, locale, stats.labelsForfaitsActifs, stats.typesForfaits, stats.abonnements )
    : { activite: [], forfaits: [] };

  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [bottomNavHeight, setBottomNavHeight] = useState(0); // si besoin

  const indicatifToPays = {
    "+33": "France",
    "+32": "Belgique",
    "+41": "Suisse",
    "+34": "Espagne",
    "+39": "Italie",
    "+1": "États-Unis",
    "+44": "Royaume-Uni",
    "+49": "Allemagne",
    "+91": "Inde",
    "+212": "Maroc",
    "+213": "Algérie",
    "+216": "Tunisie",
    "+20": "Égypte",
  };

  useEffect(() => {
    const header = document.querySelector('.header');
    const footer = document.querySelector('.footer');
    const nav = document.querySelector('.MuiBottomNavigation-root');

    const updateHeights = () => {
      if (header) setHeaderHeight(header.offsetHeight);
      if (footer) setFooterHeight(footer.offsetHeight);
      if (nav) setBottomNavHeight(nav.offsetHeight);
    };

    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, []);

  useEffect(() => {
  if (!token) {
    setUnauthorized(true);
    setLoading(false);
    return;
  }
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}`  }
        });
        logDev("📦 Réponse brute axios", res);
        logDev("[AdminDashboard] Exemple groupe brut", res.data.groupes[0]);

        const totalContacts = res.data.groupes.reduce((acc, groupe) => {
          return acc + (groupe.contacts?.length || 0);
        }, 0);

        const totalArticles = res.data.groupes.reduce((acc, groupe) => {
          return acc + (groupe.menusjournalier?.length || 0);
        }, 0);

        res.data.totalContacts = totalContacts;
        res.data.totalArticles = totalArticles;

        const groupesParType = res.data.groupes.reduce((acc, groupe) => {
        const type = groupe.abonnement?.type || (groupe.abonnementActif === false ? "gratuit" : "inconnu");
        acc[type] = (acc[type] || 0) + 1;
        return acc;
        }, {});

        res.data.groupesParType = groupesParType;
   
        const groupesParPays = res.data.groupes.reduce((acc, groupe) => {
        const tel = groupe.telephone || "";

        let pays = "Inconnu";

        if (tel.startsWith("+")) {
          for (const indicatif of Object.keys(indicatifToPays).sort((a, b) => b.length - a.length)) {
            if (tel.startsWith(indicatif)) {
              pays = indicatifToPays[indicatif];
            break;
          }
        }
      }

      acc[pays] = (acc[pays] || 0) + 1;
      return acc;
    }, {});

    res.data.groupesParPays = groupesParPays;

    logDev("🌍 Répartition des groupes par pays", groupesParPays);

        setStats(res.data);
        logDev("✅ Données finales envoyées au dashboard", res.data);
        logDev("📊 Stats admin reçues", res.data);
      } catch (err) {
          if (err.response?.status === 403) setUnauthorized(true);
        else console.error("Erreur admin:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [email]);

  if (loading) return <CircularProgress />;
  if (unauthorized) return <Typography color="error">Accès refusé</Typography>;
  if (!stats) return <Typography>Chargement des statistiques...</Typography>;

 const topOffset = headerHeight + (isMobile ? footerHeight : bottomNavHeight) + 16;
 const bottomOffset = isMobile ? bottomNavHeight : footerHeight;

  return (
    <div
    className="admin-dashboard-container"
    style={{
      minHeight: "100vh",
      paddingTop: `${topOffset}px`,
      paddingBottom: `${bottomOffset}px`,
      boxSizing: "border-box",
      backgroundColor: "#f4ece2",
      overflowY: "auto"
    }}
  >
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t("AdminDashboard")}
      </Typography>

      <Button
      variant="outlined"
      color="error"
      onClick={() => {
        localStorage.removeItem("adminToken");
        window.location.href = "/admin-login"; // ou utilise navigate() si tu as React Router
      }}
      sx={{ mb: 2 }}
      >
      {t("LogOut")}
      </Button>


      <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} sx={{ mb: 3 }}>
        <Tab label= {t("RealTime")} />
        <Tab label={t("Statistics")} />
      </Tabs>

      {tab === 0 && (
        <>
          
  <Typography variant="h6" sx={{ mt: 2 }}>📊 {t("Activity")}</Typography>
  <StatsCards cards={statsCards.activite} />

  <Typography variant="h6" sx={{ mt: 2 }}>📅 {t("SpecialPackagesAndGroups")}</Typography>
  <StatsCards cards={statsCards.forfaits} />

  <Typography variant="h6" sx={{ mt: 2 }}>📦 {t("ActivePlan")}</Typography>
  
  {Object.entries(stats.forfaitsActifs || {}).map(([type, count]) => (
    <div key={type}>{type} : {count} {t("GroupsOnly")}</div>
  ))}
  

  <Typography sx={{ mb: 2 }}>
   📅 {stats.groupes.length} {t("GroupCreatedThisMonth")}
  </Typography>

  <DataGrid
    rows={stats.groupes.map((groupe, index) => {
      const tel = groupe.telephone || "";
      let pays = "Inconnu";
        if (tel.startsWith("+")) {
            for (const indicatif of Object.keys(indicatifToPays).sort((a, b) => b.length - a.length)) {
              if (tel.startsWith(indicatif)) {
              pays = indicatifToPays[indicatif];
            break;
          }
        }
      }

  return {
    id: index,
    hashCreateur: groupe.hashCreateur?.slice(0, 8) || "—",
    nom: `${groupe.emoji || "👥"} ${groupe.nom}`,
    dateCreation: new Date(groupe.dateCreation).toLocaleDateString(),
    contacts: groupe.contacts?.length || 0,
    jours: (() => {
      const p = groupe.periodes?.[0];
      if (!p?.start || !p?.end) return 0;
      const d1 = new Date(p.start);
      const d2 = new Date(p.end);
      const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
      return diff + 1;
    })(),

    articles: groupe.articles?.length || 0,
    itemsUniques: (() => { 
      if (!groupe.items) return 0; 
      const noms = groupe.items.map(a => a.name?.trim().toLowerCase()); 
      return new Set(noms).size;
     })(),
    quantiteTotale: (() => { 
      if (!groupe.items) return 0; 
      return groupe.items.reduce((acc, item) => acc + (item.quantity || 0), 0);
     })(), 
    pays,
    type: groupe.abonnement?.type || (groupe.abonnementActif === false ? "Gratuit" : "Inconnu")
  };
})}
  columns={[
    { 
      field: "hashCreateur", 
      headerName: t("Identifier"), 
      minWidth: 90,
      flex: isMobile ? 0.6 : 0.5,
      renderCell: (params) => (
    <Typography variant="caption" color="text.secondary">
      {params.value}
    </Typography>
  ) 
    },
    
    { field: "nom", headerName: t("GroupName"), minWidth: 140, flex: 1 }, 
    { field: "dateCreation", headerName: t("Creation"), minWidth: 110, flex: 0.7 }, 
    { field: "pays", headerName: t("Country"), minWidth: 110, flex: 0.7 }, 
    { field: "type", headerName: t("Type"), minWidth: 110, flex: 0.7 }, 
    { field: "contacts", headerName: t("Contacts"), type: "number", minWidth: 80, flex: 0.5 }, 
    { field: "jours", headerName: t("NumberOfDays"), type: "number", minWidth: 100, flex: 0.6 }, 
    { field: "articles", headerName: t("Item"), type: "number", minWidth: 80, flex: 0.5 },
    { field: "itemsUniques", headerName: t("UniqueItems"), type: "number", minWidth: 120, flex: 0.7 },
    { field: "quantiteTotale", headerName: t("TotalQuantity"), type: "number", minWidth: 120, flex: 0.7 },
  ]}
  pageSizeOptions={[5, 10, 25]}
  initialState={{
    pagination: { paginationModel: { pageSize: 5, page: 0 } }
  }}
  autoHeight
  disableRowSelectionOnClick
  sx={{
    backgroundColor: "#fff",
    fontSize: isMobile ? "0.8rem" : "1rem",

    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#f5f5f5",
      fontWeight: "bold"
    }
  }}
/>

  </>
    )}
      {tab === 1 && (
        <GroupesParAnEtMois
          groupesParAn={stats.groupesParAn}
          groupesParMois={stats.groupesParMois}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
